// LibroMayorXL - Sprint 44 Fase 3 Vista 8.
// Reporte libro mayor por cuenta + rango. XLTable de movimientos con
// saldo acumulado. Solo lectura.
import { useEffect, useMemo, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import * as svc from '../services/contabilidadService';
import XLInput from '../../../components/xl/XLInput';
import XLSelect from '../../../components/xl/XLSelect';
import XLTable from '../../../components/xl/XLTable';
import { fmt } from '../utils';

const CSS_ID = 'xl-rep-lm-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 16px }

.xl-tools {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
  display: grid; grid-template-columns: 2fr 1fr 1fr auto; gap: 12px;
  align-items: end;
}
@media (max-width: 900px) { .xl-tools { grid-template-columns: 1fr } }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
}
.xl-btn:hover { background: var(--xl-color-primary-hover) }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-banner-err {
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
  padding: 10px 14px; margin-bottom: 16px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}

.xl-card-info {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 14px 16px;
  margin-bottom: 12px;
}
.xl-card-info__title {
  font-size: 14px; font-weight: 600; color: var(--xl-color-text);
}
.xl-card-info__title b { font-family: var(--xl-font-mono); margin-right: 8px }
.xl-card-info__sub {
  font-size: 12px; color: var(--xl-color-text-secondary); margin-top: 4px;
}
.xl-card-info__sub b { font-family: var(--xl-font-mono); color: var(--xl-color-text); margin-left: 4px }

.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
`;

export default function LibroMayorXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const firstDay = today.slice(0, 7) + '-01';
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState('');
  const [desde, setDesde] = useState(firstDay);
  const [hasta, setHasta] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await svc.listCuentas({ solo_hojas: true, activo: 'true' });
        if (alive) setCuentas(list || []);
      } catch (_) { if (alive) setCuentas([]); }
    })();
    return () => { alive = false; };
  }, []);

  const load = async () => {
    if (!cuentaId) return;
    setLoading(true); setErr(null);
    try { setData(await svc.libroMayor(cuentaId, desde, hasta)); }
    catch (e) { setErr((e && e.message) || 'Error'); setData(null); }
    finally { setLoading(false); }
  };

  const cuentaOptions = useMemo(() => ([
    { value: '', label: 'Selecciona cuenta...' },
    ...cuentas.map((c) => ({ value: String(c.id), label: c.codigo + ' - ' + c.nombre })),
  ]), [cuentas]);

  const columns = useMemo(() => ([
    { key: 'fecha', label: 'Fecha' },
    { key: 'referencia', label: 'Ref',
      render: (m) => <span className="xl-mono">{m.referencia || '\u2014'}</span> },
    { key: 'descripcion', label: 'Descripcion',
      render: (m) => m.descripcion || '\u2014' },
    { key: 'debito', label: 'Debito', align: 'right',
      render: (m) => <span className="xl-mono">{fmt(m.debito)}</span> },
    { key: 'credito', label: 'Credito', align: 'right',
      render: (m) => <span className="xl-mono">{fmt(m.credito)}</span> },
    { key: 'saldo_acumulado', label: 'Saldo', align: 'right',
      render: (m) => (
        <span className="xl-mono" style={{ fontWeight: 600 }}>{fmt(m.saldo_acumulado)}</span>
      ) },
  ]), []);

  return (
    <div className="xl-page">
      <h2 className="xl-page__title">Libro mayor</h2>
      <p className="xl-page__sub">Movimientos con saldo acumulado.</p>

      <section className="xl-tools" aria-label="Filtros libro mayor">
        <XLSelect label="Cuenta" value={cuentaId}
          onChange={(e) => setCuentaId(e.target.value)} options={cuentaOptions} />
        <XLInput label="Desde" type="date" value={desde}
          onChange={(e) => setDesde(e.target.value)} />
        <XLInput label="Hasta" type="date" value={hasta}
          onChange={(e) => setHasta(e.target.value)} />
        <button type="button" className="xl-btn"
          onClick={load} disabled={loading || !cuentaId}>
          {loading ? 'Cargando\u2026' : 'Generar'}
        </button>
      </section>

      {err && <div className="xl-banner-err" role="alert">{err}</div>}

      {data && (
        <>
          <div className="xl-card-info">
            <div className="xl-card-info__title">
              <b>{data.cuenta && data.cuenta.codigo}</b>
              {data.cuenta && data.cuenta.nombre}
            </div>
            <div className="xl-card-info__sub">
              Naturaleza: {data.cuenta && data.cuenta.naturaleza} \u00b7 Saldo final:
              <b>{fmt(data.saldo_final)}</b>
            </div>
          </div>
          <XLTable
            ariaLabel="Libro mayor"
            columns={columns}
            data={data.items || []}
            loading={loading}
            rowKey="id"
            emptyMessage="Sin movimientos." />
        </>
      )}
    </div>
  );
}
